import * as React from "react";

// Canonical responsive tiers. See MOBILE_GUIDELINES.md.
//   < 768  → Mobile  (single column, no chrome)
//   768–1279 → Compact (mobile-style chrome, sidebar visible, no right rail)
//   ≥ 1280 → Wide   (full desktop: sidebar + main + right rail)
export const MOBILE_BREAKPOINT = 768;
export const WIDE_BREAKPOINT = 1280;

export function useIsMobile() {
  return useIsBelow(MOBILE_BREAKPOINT);
}

/**
 * True when viewport is at or above the Wide tier (≥ 1280px).
 * Use this to gate desktop-only chrome — right rails, multi-pane layouts,
 * three-column grids — anywhere a hand-rolled `1280px` media query would
 * otherwise live.
 */
export function useIsWide() {
  return useIsAtLeast(WIDE_BREAKPOINT);
}

/**
 * The Wide tier BEFORE the `!!` — `undefined` until the mount effect below has
 * measured the viewport, then the real answer.
 *
 * `useIsWide()` collapses that `undefined` to `false`, which is right for layout
 * (render the narrow variant, swap on the next tick) and wrong for an effect
 * that BRANCHES on the tier and then consumes the thing that triggered it: it
 * would pick the narrow branch on a Wide viewport, on the first render of every
 * fresh mount, and never get a second chance. Use this when the answer has to be
 * correct rather than merely available, and early-return while it is undefined.
 *
 * Deliberately NOT fixed by seeding the state from `matchMedia` at init: that
 * changes first-render behaviour for every surface in the app, and `mql.matches`
 * disagrees with `window.innerWidth` by the scrollbar width right at the
 * boundary.
 */
export function useIsWideResolved(): boolean | undefined {
  return useAtLeastState(WIDE_BREAKPOINT);
}

export function useIsBelow(breakpoint: number) {
  const [isBelow, setIsBelow] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => {
      setIsBelow(window.innerWidth < breakpoint);
    };
    mql.addEventListener("change", onChange);
    setIsBelow(window.innerWidth < breakpoint);
    return () => mql.removeEventListener("change", onChange);
  }, [breakpoint]);

  return !!isBelow;
}

/** The raw tier state, `undefined` until the mount effect has measured. */
function useAtLeastState(breakpoint: number): boolean | undefined {
  const [isAtLeast, setIsAtLeast] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const onChange = () => {
      setIsAtLeast(window.innerWidth >= breakpoint);
    };
    mql.addEventListener("change", onChange);
    setIsAtLeast(window.innerWidth >= breakpoint);
    return () => mql.removeEventListener("change", onChange);
  }, [breakpoint]);

  return isAtLeast;
}

export function useIsAtLeast(breakpoint: number) {
  return !!useAtLeastState(breakpoint);
}
